import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { loadLastShare, saveLastShare } from "../constants/giftJourney";
import { auth, db } from "../api/firebase";
import { get, push, ref, set, update } from "firebase/database";
import GiftDetailsLayout from "../components/GiftDetailsLayout";
import "../styles/journey.css";
import "../styles/productDetails.css";

function buildShareText(path, sweetMessage) {
  const msg = String(sweetMessage || "").trim();
  return `${msg}\n\nOpen your e-card here:\n${path}\n\nSent with GifteKart`;
}

function formatRemaining(ms) {
  if (ms <= 0) return "Now";
  const mins = Math.floor(ms / 60000);
  const hrs = Math.floor(mins / 60);
  const days = Math.floor(hrs / 24);
  if (days > 0) return `${days}d ${hrs % 24}h`;
  if (hrs > 0) return `${hrs}h ${mins % 60}m`;
  return `${mins}m`;
}

export default function ProductShare() {
  const { id } = useParams();
  const location = useLocation();
  const [copyHint, setCopyHint] = useState("");
  const [publishBusy, setPublishBusy] = useState(false);
  const [publishErr, setPublishErr] = useState("");
  const [sweetMessage, setSweetMessage] = useState("A little surprise for you, made with love.");

  const merged = useMemo(
    () => ({
      ...(loadLastShare(id) || {}),
      ...(location.state || {}),
    }),
    [location.state, id]
  );
  const [shareMeta, setShareMeta] = useState(merged);
  const {
    giftKey,
    giftkartCategoryId,
    internalOrderId,
    shortCode,
    previewGift,
    privateDraftId,
  } = shareMeta;
  const schedule = shareMeta.schedule || null;
  const [nowMs, setNowMs] = useState(Date.now());

  const origin =
    typeof window !== "undefined" ? window.location.origin : "";
  const shortPath = shortCode ? `${origin}/s/${shortCode}` : null;
  const fallbackPath =
    giftKey && giftkartCategoryId ? `${origin}/${giftkartCategoryId}/${giftKey}` : null;
  const cardPath = shortPath || fallbackPath;
  const scheduleMs = Number(schedule?.scheduledAt || 0);
  const isScheduled = Boolean(schedule?.enabled && scheduleMs);
  const scheduleDue = !isScheduled || nowMs >= scheduleMs;
  const selectedMethod = String(schedule?.method || "anywhere");

  useEffect(() => {
    if (!isScheduled) return undefined;
    const int = window.setInterval(() => setNowMs(Date.now()), 30000);
    return () => window.clearInterval(int);
  }, [isScheduled]);

  const flashHint = (msg) => {
    setCopyHint(msg);
    window.setTimeout(() => setCopyHint(""), 2500);
  };

  const ensurePublished = useCallback(async () => {
    if (cardPath) return cardPath;
    if (!privateDraftId || !giftkartCategoryId) {
      throw new Error("Card draft is missing checkout details. Please retry payment flow.");
    }
    if (!auth?.currentUser?.uid || !db) {
      throw new Error("Please login again before sharing.");
    }
    setPublishErr("");
    setPublishBusy(true);
    try {
      const uid = auth.currentUser.uid;
      const draftRef = ref(db, `users/${uid}/ecard_drafts/${privateDraftId}`);
      const draftSnap = await get(draftRef);
      const draftData = draftSnap.val();
      if (!draftData?.gift) {
        throw new Error("Private draft not found.");
      }

      const row = {
        ...draftData.gift,
        ownerUid: uid,
        internalOrderId: internalOrderId || null,
        createdAt: draftData.createdAt || new Date().toISOString(),
      };

      const publicRef = push(ref(db, `giftkart/${giftkartCategoryId}`));
      const createdGiftKey = publicRef.key;
      if (!createdGiftKey) {
        throw new Error("Could not create public gift key.");
      }
      await set(publicRef, row);

      let generatedCode = "";
      const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789";
      const cryptoApi = window.crypto || window.msCrypto;
      if (!cryptoApi?.getRandomValues) {
        throw new Error("Secure random generator not available in this browser.");
      }
      for (let i = 0; i < 8; i += 1) {
        const code = Array.from(
          cryptoApi.getRandomValues(new Uint8Array(7)),
          (n) => alphabet[n % alphabet.length]
        ).join("");
        const shortRef = ref(db, `short_links/${code}`);
        const shortSnap = await get(shortRef);
        if (shortSnap.exists()) continue;
        await set(shortRef, {
          uid,
          giftkartCategoryId,
          giftKey: createdGiftKey,
          privateDraftId,
          createdAt: new Date().toISOString(),
        });
        generatedCode = code;
        break;
      }
      if (!generatedCode) {
        throw new Error("Could not generate short link. Please retry.");
      }

      await update(draftRef, {
        status: "published",
        publishedAt: new Date().toISOString(),
        publishedGiftKey: createdGiftKey,
        publishedGiftkartCategoryId: giftkartCategoryId,
        shortCode: generatedCode,
      });
      const next = {
        ...shareMeta,
        giftKey: createdGiftKey,
        giftkartCategoryId,
        shortCode: generatedCode,
        hadPublish: true,
      };
      setShareMeta(next);
      saveLastShare(id, next);
      return `${origin}/s/${generatedCode}`;
    } catch (err) {
      const msg = err?.message || "Could not publish the card publicly.";
      setPublishErr(msg);
      throw new Error(msg);
    } finally {
      setPublishBusy(false);
    }
  }, [
    cardPath,
    giftkartCategoryId,
    id,
    internalOrderId,
    origin,
    privateDraftId,
    shareMeta,
  ]);

  useEffect(() => {
    if (cardPath || !privateDraftId || !giftkartCategoryId || publishBusy || publishErr) {
      return;
    }
    ensurePublished().catch(() => {
      /* error already exposed in publishErr */
    });
  }, [
    cardPath,
    ensurePublished,
    giftkartCategoryId,
    privateDraftId,
    publishBusy,
    publishErr,
  ]);

  const copyLinkOnly = async () => {
    let livePath = cardPath;
    try {
      if (!livePath) livePath = await ensurePublished();
      await navigator.clipboard.writeText(livePath);
      flashHint("Link copied.");
    } catch (err) {
      window.prompt("Copy this link:", livePath || String(err?.message || ""));
    }
  };

  const copyMessageAndLink = async () => {
    let livePath = cardPath;
    try {
      if (!livePath) livePath = await ensurePublished();
      await navigator.clipboard.writeText(buildShareText(livePath, sweetMessage));
      flashHint("Message and link copied.");
    } catch {
      if (livePath) {
        window.prompt("Copy this text:", buildShareText(livePath, sweetMessage));
      }
    }
  };

  const waShare = async () => {
    try {
      const livePath = cardPath || (await ensurePublished());
      const url = `https://wa.me/?text=${encodeURIComponent(buildShareText(livePath, sweetMessage))}`;
      window.open(url, "_blank");
    } catch {
      /* publishErr already shown */
    }
  };

  const emailShare = async () => {
    try {
      const livePath = cardPath || (await ensurePublished());
      const subject = encodeURIComponent("A digital card for you");
      const body = encodeURIComponent(buildShareText(livePath, sweetMessage));
      window.location.href = `mailto:?subject=${subject}&body=${body}`;
    } catch {
      /* publishErr already shown */
    }
  };

  const sendAnywhere = async () => {
    try {
      const livePath = cardPath || (await ensurePublished());
      const text = buildShareText(livePath, sweetMessage);
      if (navigator.share) {
        await navigator.share({
          title: "A digital card for you",
          text,
          url: livePath,
        });
        return;
      }
      await navigator.clipboard.writeText(text);
      flashHint("Copied. Paste it in any app.");
    } catch {
      /* publishErr already shown */
    }
  };

  const sendBySelectedMethod = async () => {
    if (selectedMethod === "whatsapp") {
      await waShare();
      return;
    }
    if (selectedMethod === "email") {
      await emailShare();
      return;
    }
    if (selectedMethod === "instagram") {
      await copyMessageAndLink();
      flashHint("Copied for Instagram. Paste in DM or Story link sticker.");
      return;
    }
    await sendAnywhere();
  };

  return (
    <div className="pd-wrap">
      <div className="journey-panel journey-share">
        <h1>Payment complete</h1>
        {!cardPath && !internalOrderId && (
          <p className="pd-error journey-share-warn">
            We could not recover your card draft metadata. Please restart from checkout.
          </p>
        )}
        {publishErr ? <p className="pd-error journey-share-warn">{publishErr}</p> : null}

        {isScheduled ? (
          <div className="journey-instagram-hint" style={{ marginBottom: "0.9rem" }}>
            <strong>Scheduled delivery:</strong>{" "}
            {scheduleDue
              ? "Your occasion time is reached. Share now using the selected method."
              : `Scheduled for ${new Date(scheduleMs).toLocaleString()} (${formatRemaining(
                  scheduleMs - nowMs
                )} left).`}
            <br />
            <strong>Selected method:</strong> {selectedMethod}
          </div>
        ) : null}

        {previewGift && giftkartCategoryId ? (
          <div className="journey-share-preview-wrap">
            <h2 className="journey-share-subhead">Preview your e-card</h2>
            <div className="journey-share-preview-shell">
              <GiftDetailsLayout gift={previewGift} categoryId={giftkartCategoryId} isMini />
            </div>
          </div>
        ) : null}

        <p className="journey-share-lead">
          Add a sweet message, then share your short link by copy, WhatsApp, email, Instagram, or
          any app.
        </p>

        <label className="journey-field" htmlFor="share-sweet-message">
          <span>Sweet message</span>
          <textarea
            id="share-sweet-message"
            value={sweetMessage}
            onChange={(e) => setSweetMessage(e.target.value)}
            rows={3}
            placeholder="Write a short message they will read before opening your card"
          />
        </label>

        <label className="journey-share-link-label" htmlFor="ecard-share-url">
          Your share link
        </label>
        <div className="journey-share-link-row">
          <input
            id="ecard-share-url"
            className="journey-share-link-input"
            readOnly
            value={cardPath || "Click any share option below to generate your short link"}
            onFocus={(e) => e.target.select()}
          />
          <button
            type="button"
            className="pd-btn pd-btn-pay journey-share-copy-btn"
            onClick={copyLinkOnly}
            disabled={publishBusy}
          >
            {publishBusy ? "Preparing…" : "Copy link"}
          </button>
        </div>
        {copyHint ? <p className="journey-share-copy-hint" role="status">{copyHint}</p> : null}

        {cardPath ? (
          <p className="journey-hint-small" style={{ marginTop: "0.75rem" }}>
            <a href={cardPath} target="_blank" rel="noreferrer">
              Open card in new tab
            </a>{" "}
            to preview exactly what your recipient will see.
          </p>
        ) : null}

        <div className="journey-share-actions" style={{ marginTop: "1.25rem" }}>
          {isScheduled ? (
            <button
              type="button"
              className="pd-btn pd-btn-pay"
              disabled={!scheduleDue || publishBusy}
              onClick={sendBySelectedMethod}
            >
              {scheduleDue ? "Share now with selected method" : "Waiting for occasion time"}
            </button>
          ) : null}
          <button
            type="button"
            className="pd-btn pd-btn-schedule"
            onClick={copyMessageAndLink}
            disabled={publishBusy || (isScheduled && !scheduleDue)}
          >
            Copy message + link
          </button>
          <button
            type="button"
            className="pd-btn pd-btn-wa"
            onClick={waShare}
            disabled={publishBusy || (isScheduled && !scheduleDue)}
          >
            Send via WhatsApp
          </button>
          <button
            type="button"
            className="pd-btn journey-btn-mail"
            onClick={emailShare}
            disabled={publishBusy || (isScheduled && !scheduleDue)}
          >
            Send via email
          </button>
          <button
            type="button"
            className="pd-btn pd-btn-share"
            onClick={sendAnywhere}
            disabled={publishBusy || (isScheduled && !scheduleDue)}
          >
            Send anywhere
          </button>
        </div>

        <div className="journey-instagram-hint">
          <strong>Instagram:</strong> tap <strong>Copy message + link</strong>, then paste in a DM
          or Story link sticker.
        </div>

        <Link to="/" className="journey-link-back journey-share-home">
          Create another card
        </Link>
      </div>
    </div>
  );
}
